import { useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle,
         DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger,
         SelectValue } from "@/components/ui/select";
import { Archive } from "lucide-react";
import { VIS, fmtBytes } from "./constants";

/**
 * Bulk-import a .zip. Backend rejects path traversal, disallowed mimes,
 * oversize files, and `__MACOSX/` resource-fork entries; we surface a
 * per-file green/amber/red summary once the extraction completes.
 */
export function ZipUploadDialog({ open, onOpenChange, currentFolder,
                                   form, setForm, busy, result, onPickFile }) {
  const zipRef = useRef(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[520px]"
                     data-testid="docs-zip-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="h-4 w-4 text-orange-400" /> Bulk upload via ZIP
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Extracts every file inside a .zip into
            {currentFolder ? ` "${currentFolder.name}"` : " the root"}.
            Nested folders are flattened; unsafe paths and unsupported types are skipped.
            Max 100 MB · per-file cap 25 MB.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div>
            <Label className="text-xs text-zinc-400">Visibility for every extracted file</Label>
            <Select value={form.visibility} onValueChange={(v) => setForm({ visibility: v })}>
              <SelectTrigger className="bg-white/[0.04] border-white/10"
                             data-testid="docs-zip-visibility">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                {VIS.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <input ref={zipRef} type="file" hidden
                 accept=".zip,application/zip,application/x-zip-compressed"
                 onChange={(e) => onPickFile(e.target.files?.[0], zipRef)}
                 data-testid="docs-zip-file-input" />
          {result && (
            <div className="border border-white/10 rounded-md p-2 max-h-56 overflow-y-auto text-xs space-y-1"
                 data-testid="docs-zip-result">
              <div className="flex gap-2 mb-1">
                <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 text-[10px]">
                  {result.counts?.uploaded || 0} uploaded
                </Badge>
                <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[10px]">
                  {result.counts?.rejected || 0} rejected
                </Badge>
                <Badge variant="outline" className="border-red-500/40 text-red-300 text-[10px]">
                  {result.counts?.failed || 0} failed
                </Badge>
              </div>
              {(result.results || []).map((r, idx) => (
                <div key={`${r.name}-${idx}`} className="flex items-center gap-2">
                  <span className={
                    r.status === "uploaded" ? "text-emerald-300"
                    : r.status === "rejected" ? "text-amber-300"
                    : "text-red-300"
                  }>●</span>
                  <span className="text-zinc-300 truncate flex-1">{r.name}</span>
                  <span className="text-zinc-500 shrink-0">
                    {r.status === "uploaded" ? fmtBytes(r.size) : (r.reason || r.status)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  disabled={busy} className="border-white/10">
            {result ? "Close" : "Cancel"}
          </Button>
          <Button onClick={() => zipRef.current?.click()} disabled={busy}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="docs-zip-pick-file">
            {busy ? "Extracting…" : (result ? "Upload another ZIP" : "Pick .zip & extract")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
